import cn from 'classnames'
import * as React from 'react'

import { ErrorText } from '../form'

import styles from './autosuggest.module.scss'
import { FormattedMessage } from 'react-intl'
import { faSearch } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Suggestion = any

interface IAutoSuggestProps {
  highlighted: number
  suggestions: Suggestion[]
  renderSuggestion: (suggestion: Suggestion, i: number) => React.ReactNode
  label?: string
  labelRight?: string | React.ReactNode
  errorCollapse?: 'dynamic' | 'fixed'
  valid?: string
  errorMessage?: string
  icon?: 'search'
  extra?: React.ReactNode[]
  hideIcon?: boolean
  placeholder?: string
}

const icons = {
  search: <FontAwesomeIcon className={styles.icon} icon={faSearch} />,
}

export default function AutoSuggest({
  errorCollapse = 'fixed',
  errorMessage = '',
  label = '',
  labelRight = '',
  name,
  valid = 'unknown',
  suggestions,
  renderSuggestion,
  icon,
  extra,
  hideIcon,
  highlighted,
  ...rest
}: IAutoSuggestProps & React.InputHTMLAttributes<HTMLInputElement>) {
  const ref = React.useRef<HTMLDivElement | null>(null)

  if (typeof name !== 'string') {
    throw new TypeError('Name must be a string')
  }

  const isInvalid = valid === 'invalid'

  React.useEffect(() => {
    if (ref.current) {
      const nodes = Array.prototype.slice.call(ref.current.children)
      const node = nodes[highlighted]
      ref.current.scrollTop = node.offsetTop
    }
  }, [highlighted, ref])

  return (
    <div className={styles.root}>
      <div className={styles.labels}>
        {label && (
          <label>
            {label} {!rest.required && <FormattedMessage defaultMessage="(frivilligt)" id="input.optional-text" />}
          </label>
        )}

        {labelRight && (typeof labelRight === 'string' ? <label htmlFor="">{labelRight}</label> : labelRight)}
      </div>

      <input
        className={cn([styles.input, isInvalid && styles.error])}
        style={{ paddingRight: icon ? '52px' : 'auto' }}
        {...rest}
        placeholder={rest.required ? rest.placeholder + ' *' : rest.placeholder}
      />
      {(errorCollapse === 'fixed' || isInvalid) && <ErrorText>{isInvalid && errorMessage}</ErrorText>}
      {icon && !hideIcon && icons[icon]}

      {suggestions && suggestions.length ? (
        <div className={styles.suggestions} ref={ref}>
          {renderSuggestion && suggestions.map((suggestion: Suggestion, i: number) => renderSuggestion(suggestion, i))}

          {extra && extra.map((ex: React.ReactNode) => ex)}
        </div>
      ) : null}
    </div>
  )
}
